import { addMonths, format } from 'date-fns'

interface FixedPeriod {
  period_fixed_length_months?: number | null
  period_fixed_end_date?: string | null
}

/**
 * Converts each period's length in months into an end date, chained from the previous period's end date
 * (or from the start date for the first period). An explicit end date wins over a length, and a negative
 * length marks an unbounded period, which gets a null end date.
 * Every returned period has period_fixed_length_months set to null, so callers that post periods back
 * must re-derive any length markers themselves.
 * @param arr ERC bands or rate tiers
 * @param startDate The date the first period starts on, defaulting to today
 */
export const addPeriodFixedEndDate = <T extends FixedPeriod>(arr?: T[] | null, startDate?: string | null) => {
  const mortgageStartDate = startDate || format(new Date(), 'yyyy-MM-dd')

  return arr?.reduce<T[]>((acc, period) => {
    const { period_fixed_length_months, period_fixed_end_date } = period
    const prevEndDate = acc.length > 0 ? acc[acc.length - 1].period_fixed_end_date : mortgageStartDate

    const currentEndDate = (() => {
      if (period_fixed_end_date) {
        return period_fixed_end_date
      }

      if (period_fixed_length_months && period_fixed_length_months < 0) {
        return null
      }

      if (prevEndDate && period_fixed_length_months) {
        return format(addMonths(new Date(prevEndDate), period_fixed_length_months), 'yyyy-MM-dd')
      }

      return null
    })()

    return [
      ...acc,
      {
        ...period,
        period_fixed_length_months: null,
        period_fixed_end_date: currentEndDate,
      },
    ]
  }, [])
}
